import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk, RootState } from '../..'
import { getAccess } from '../../api/access'
import {
  AccessControls,
  AccessControlsResponse,
  PermissionLevels,
  PermissionLevelsMap,
  ViewPermissions,
} from '../../types/AccessControls'
import { showErrorNotification } from '../notifications/actions'

interface AccessControlState {
  data: AccessControls | undefined
  hasError: boolean
  isLoading: boolean
  // Incremented on every reset (i.e. logout). In-flight requests capture it and discard
  // their response if it no longer matches, so one user's permissions can never land in
  // the next user's store when accounts are switched without a page reload.
  session: number
}

const initialState: AccessControlState = {
  data: undefined,
  isLoading: false,
  hasError: false,
  session: 0,
}

export const accessControlsSlice = createSlice({
  name: 'accessControls',
  initialState,
  reducers: {
    requestAccess(state) {
      state.isLoading = true
      state.hasError = false
    },
    requestedAccessSucceeded(state) {
      return {
        ...state,
        isLoading: false,
        hasError: false,
      }
    },
    saveAccess(state, action: PayloadAction<AccessControls>) {
      const newAccessControls = { ...state.data, ...action.payload }
      return {
        ...state,
        data: newAccessControls,
      }
    },

    saveViewAccess(state, action: PayloadAction<ViewPermissions>) {
      const newAccessControls: AccessControls = { ...state.data, viewPermissions: action.payload }
      return {
        ...state,
        data: newAccessControls,
      }
    },

    savePageAccess(state, action: PayloadAction<PermissionLevelsMap>) {
      // No `state.data` guard here. On a refresh, page components mount and request their
      // permission level before the current-user saga has populated `data` with the view
      // permissions. Dropping the payload in that window means the level is never cached,
      // so every re-render re-dispatches the fetch in an endless loop.
      const newAccessControls: AccessControls = {
        ...state.data,
        permissionLevels: {
          ...state.data?.permissionLevels,
          ...action.payload,
        },
      }
      return {
        ...state,
        data: newAccessControls,
      }
    },
    requestedAccessFailed(state) {
      state.isLoading = false
      state.hasError = true
    },
    resetAccessControl(state) {
      return { ...initialState, session: state.session + 1 }
    },
  },
})

export const {
  requestAccess,
  requestedAccessSucceeded,
  requestedAccessFailed,
  savePageAccess,
  saveViewAccess,
  saveAccess,
  resetAccessControl,
} = accessControlsSlice.actions

export const selectAccessControl = (state: RootState) => state.accessControls

export const fetchAccessControls =
  (permissionLevel: PermissionLevels): AppThunk =>
    async (dispatch, getState) => {
      const requestSession = getState().accessControls.session
      try {
        const response = await getAccess(permissionLevel)
        // Drop the response if the session was reset while it was in flight.
        if (getState().accessControls.session !== requestSession) return
        if (response) {
          const accessControlData: AccessControlsResponse = response.data
          dispatch(
            savePageAccess({
              // Always store an array, even when the API returns no permissions for this
              // level. A falsy value here would keep `isAlreadyLoaded` false in
              // `loadAccessControls` and cause the level to be refetched endlessly.
              [permissionLevel]: accessControlData.permissions ?? [],
            }),
          )
          dispatch(requestedAccessSucceeded())
        }
      } catch (e) {
        if (getState().accessControls.session !== requestSession) return
        dispatch(requestedAccessFailed())
        dispatch(showErrorNotification('Not able to fetch permissions. Please try again later'))
      }
    }

// Levels with a request already in flight, mapped to the session they belong to. Several
// components on the same page ask for the same level on mount, and the store is only
// updated once the response lands, so without this the same endpoint is hit once per
// consumer. Keying by session means a stale entry left over from a previous login never
// blocks the new user's fetch.
const inFlightPermissionLevels = new Map<PermissionLevels, number>()

export const loadAccessControls =
  (permissionLevel: PermissionLevels): AppThunk =>
    async (dispatch, getState) => {
      // Use Redux as the only in-memory cache for the current session.
      // Always fetch from API if not already loaded — no sessionStorage dependency.
      const { accessControls } = getState()
      const isAlreadyLoaded = !!accessControls.data?.permissionLevels?.[permissionLevel]
      const isInFlight = inFlightPermissionLevels.get(permissionLevel) === accessControls.session
      if (isAlreadyLoaded || isInFlight) return

      // Deliberately not dispatching `requestAccess()` here. That flips the shared
      // `isLoading` flag, which `IndexLayout` uses to blank the entire app behind a
      // full-screen loader while "view access is fetched". Page-level fetches happen on
      // every route, so flipping it hides the whole UI (navbar included) on each
      // navigation — and leaves it hidden if a request never settles. Page-level loading
      // is already reported per level by useAccessGrant / useAccessControl.
      inFlightPermissionLevels.set(permissionLevel, accessControls.session)
      try {
        await dispatch(fetchAccessControls(permissionLevel))
      } finally {
        inFlightPermissionLevels.delete(permissionLevel)
      }
    }

export default accessControlsSlice.reducer
